import type { EpisodeHeaderProps } from '@ksyos/design-system/dist/components/episode-header';
import { Alert, Box, Divider, EpisodeHeader, FormGroupContext, Link, Text } from '@ksyos/design-system';
import { bind } from 'bind-decorator';
import { Map } from 'immutable';
import { isEmpty, uniq } from 'lodash';
import { Fragment } from 'react';
import { connect } from 'react-redux';
import type { ServiceUrlObject } from '@common/interfaces/serviceUrlObject';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { ManagerAuthorization, TaskStatus, TaskType } from '@common/enums';
import { UseUIStateWrapper } from '@frontend/components/contexts/uiState/UseUIStateWrapper';
import { EpisodeHeaderTabBar } from '@frontend/components/elements/EpisodeHeaderTabBar';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { episodeCategoryIconName } from '@frontend/components/elements/Icons/episodeCategoryIconName';
import { Recommendations } from '@frontend/components/elements/recommendations/Recommendations';
import { ViewMedicalDossierPatientConsentModal } from '@frontend/components/elements/viewMedicalDossierPatientConsentModal/ViewMedicalDossierPatientConsentModal';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getProcesses } from '@frontend/services/api/patientsApiService';
import { getRequestProcesses } from '@frontend/services/api/requestProcessesApiService';
import {
    hasOneAuthorization,
    scrollContainerId,
    scrollToBottomWithinContainer,
    scrollToWithinContainer,
} from '@frontend/utils';
import { checkForProcessesClosedByOtherRequesterOrganizations } from '@frontend/utils/checkForProcessesClosedByOtherRequesterOrganizations';
import { setViewName, startView } from '@frontend/utils/hooks/useDatadogView';
import { FrontendSettingsContext } from '@frontend/utils/hooks/useFrontendSettings';
import { generateUrlForRoute } from '@frontend/utils/hooks/useNavigateTo';
import type { ReportCategories } from '../../../../common/enums/reportCategories';
import type { IPatient } from '../../../../common/interfaces/patient';
import type { IServiceStartingPoint } from '../../../../common/interfaces/serviceWorkflow';
import type { IReduxState } from '../../../configureStore';
import type { IBreadcrumbItem } from '../../elements/common';
import type { IProcedure } from '../../interfaces/procedure';
import type { IProcess } from '../../interfaces/process';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { IEpisodeContext } from './EpisodeContext';
import { EpisodeCategory } from '../../../../common/enums/episodeCategory';
import { ProcedureStatus } from '../../../../common/enums/procedureStatus';
import { ProcessScrollItem } from '../../../../common/enums/processScrollItem';
import { RequestStatus } from '../../../../common/enums/requestStatus';
import { ServiceType } from '../../../../common/enums/serviceType';
import { SupervisionStatus } from '../../../../common/enums/supervisionStatus';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { compareByServiceTypeButtonSortOrder } from '../../../../common/utils/serviceSortUtils';
import { AbortableFetch, fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { isActiveProcess } from '../../../utils/isActiveProcess';
import { isOpenActiveProcessesEnabledByEpisodeCategory } from '../../../utils/isOpenActiveProcessesEnabledByEpisodeCategory';
import { Button, Loading, Paragraph } from '../../elements/common';
import { Component } from '../../elements/Component';
import { FirstUseTour } from '../../elements/firstUseTour/FirstUseTour';
import { Modal } from '../../elements/modals/Modal';
import { ProcessStartWizard } from '../../elements/ProcessStartWizard/ProcessStartWizard';
import { NewProcessApi } from '../home/newProcess/api/newProcessApi';
import { NewProcessButtonStartBar } from '../home/newProcess/NewProcessButtonStartBar';
import { PatientAccessReasons } from '../patients/PatientAccessReasons';
import { PatientAvatarBlock } from '../patients/PatientAvatarBlock';
import { PatientActiveInsuranceContextProvider } from '../patients/PatientInsuranceHistoryContext';
import { PatientSideBar } from '../patients/patientSideBar/PatientSideBar';
import { ProcedureItemAddEdit } from '../procedures/ProcedureItemAddEdit';
import { RequestItemAddEdit } from '../requests/RequestItemAddEdit';
import { CollapseWrapper } from './common';
import { EmptyEpisode } from './EmptyEpisode';
import { EpisodeContextProvider } from './EpisodeContextProvider';
import { Process } from './Process';
import { ReloadEpisode } from './reloadEpisode';

interface IEpisodeReduxProps {
    authentication: {
        id?: string;
        organizationId?: string;
        organizationType: string;
        selectedEmploymentId: string;
    };
}

export interface IPreviousRequestWithProcedure extends IRequestGetOne {
    procedure: IProcedure;
}

interface IEpisodeState {
    add?: {
        newProcessCustomIdentifier: string | undefined;
        previousRequestId?: string;
        serviceId: string;
        withProcedure: boolean;
    };
    allProcessesLoaded?: boolean;
    episodeCategory: EpisodeCategory | null;
    hasFlexProcessesForCarePath: boolean;
    hasServices?: boolean;
    hideCreateActions: boolean;
    isExternalHolter?: string;
    isViewMedicalDossierPatientConsentModalOpen: boolean;
    location: {
        query: {
            editProcedure?: string;
            episodeCategory?: EpisodeCategory;
            isExternalHolter?: string;
            newProcessCustomIdentifier?: string;
            patientId?: string;
            requestId?: string;
            serviceId?: string;
            withProcedure?: string;
        };
    };

    newProcessCancelled?: boolean;
    patient?: IPatient;

    patientHasMedicalHistory: boolean;
    previousRequest?: IPreviousRequestWithProcedure;
    processes: IProcess[];
    processItemStates: Map<string, IProcessItemState>;

    requestItemAddEditLoaded?: boolean;

    scrolledToSelectedProcessOnLoaded?: boolean;
    selectedProcessWasCancelled: boolean;
    selectedServiceType?: ServiceType;
    serviceTypes: ServiceType[];
    settingUpEpisode: boolean;
    showModalForCancelledProcess: boolean;
    showNoDefaultMessage?: boolean;
    showRecommendations: boolean;
}

export interface IProcessItemState {
    diagnosticReports: Map<string, IReportItemState>;
    newReportCategory?: ReportCategories;
    procedure?: IItemState;
    request: IItemState;
    supervision?: IItemState;
}

export interface IItemState {
    initialIsOpen?: boolean;
    isFirstProcess?: boolean;
    isGradingOpen?: boolean;
    isOpen: boolean;
    mode: 'add' | 'edit' | 'read';
}

export interface IReportItemState extends IItemState {
    addClarification: boolean;
    isClarificationOpen: boolean;
}

const mapStateToProps = ({ authentication }: IReduxState): IEpisodeReduxProps => ({
    authentication: {
        id: authentication.id,
        organizationId: authentication.organizationId,
        organizationType: authentication.organizationType,
        selectedEmploymentId: authentication.selectedEmploymentId,
    },
});

const newProcessId = 'newProcessFormId';

class InnerEpisode extends Component<IWithRouterProps & IEpisodeReduxProps, IEpisodeState> {
    static contextType = FrontendSettingsContext;
    declare context: React.ContextType<typeof FrontendSettingsContext>;

    private numberOfProcessesStillLoading: number;
    private readonly newProcessApi = new NewProcessApi();
    private readonly abortableFetch = new AbortableFetch();
    private readonly abortController = new AbortController();

    constructor(props: IWithRouterProps & IEpisodeReduxProps, context: unknown) {
        super(props, context);

        this.state = {
            episodeCategory: null,
            hasFlexProcessesForCarePath: false,
            hideCreateActions: false,
            isViewMedicalDossierPatientConsentModalOpen: false,
            location: {
                query: {
                    editProcedure: this.props.router.searchParams.getOptional('editProcedure'),
                    episodeCategory: this.props.router.searchParams.getOptional('episodeCategory') as
                        | EpisodeCategory
                        | undefined,
                    newProcessCustomIdentifier:
                        this.props.router.searchParams.getOptional('newProcessCustomIdentifier'),
                    patientId: this.props.router.searchParams.getOptional('patientId'),
                    requestId: this.props.router.searchParams.getOptional('requestId'),
                    serviceId: this.props.router.searchParams.getOptional('serviceId'),
                    withProcedure: this.props.router.searchParams.getOptional('withProcedure'),
                },
            },
            patientHasMedicalHistory: false,
            processItemStates: Map(),
            processes: [],
            selectedProcessWasCancelled: false,
            serviceTypes: [],
            settingUpEpisode: true,
            showModalForCancelledProcess: false,
            showRecommendations: false,
        };

        this.numberOfProcessesStillLoading = 0;
    }

    public async componentDidMount() {
        await this.load();
        await this.loadTypeButtons();
        if (!this.state.add) {
            setViewName(`/episode/${this.state.episodeCategory}`);
        }
    }

    public componentWillUnmount() {
        this.abortableFetch.abort();
        this.abortController.abort();
    }

    public componentDidUpdate(_prevProps: unknown, prevState: IEpisodeState) {
        const oldStateHasAddOrEditMode = this.hasAddOrEditModeInProcessItemState(prevState.processItemStates);
        const newStateHasAddOrEditMode = this.hasAddOrEditModeInProcessItemState(this.state.processItemStates);

        const oldStateHasAdd = prevState.add !== undefined;
        const newStateHasAdd = this.state.add !== undefined;

        if ((oldStateHasAddOrEditMode && !newStateHasAddOrEditMode) || (oldStateHasAdd && !newStateHasAdd)) {
            // transition from add/edit mode to read mode
            startView(`/episode/${this.state.episodeCategory}`);
        }
    }

    public async load() {
        await this.setState({ settingUpEpisode: true });
        let process;
        let patientId;
        // set patient in state
        if (this.state.location.query.patientId) {
            patientId = this.state.location.query.patientId;
            await this.loadPatient(this.state.location.query.patientId);
        } else if (this.state.location.query.requestId) {
            process = await this.loadProcess(this.state.location.query.requestId);
            patientId = process.patientId;
            await this.loadPatient(process.patientId);
        } else {
            throw Error('Missing patientId or requestId');
        }

        // set processes in state
        if (this.state.location.query.serviceId) {
            if (this.state.location.query.episodeCategory) {
                await this.loadProcesses(patientId, this.state.location.query.episodeCategory);
            } else if (this.state.location.query.requestId) {
                if (!process) {
                    process = await this.loadProcess(this.state.location.query.requestId);
                }
                if (process.episodeCategory) {
                    await this.loadProcesses(patientId, process.episodeCategory);
                } else {
                    await this.setState({ processes: [process] });
                }
            }

            // serviceId and withProcedure from props are initial values
            await this.setState({
                add: {
                    newProcessCustomIdentifier: this.state.location.query.newProcessCustomIdentifier,
                    serviceId: this.state.location.query.serviceId,
                    withProcedure: this.state.location.query.withProcedure === 'true',
                },
            });
        } else if (this.state.location.query.episodeCategory) {
            await this.loadProcesses(patientId, this.state.location.query.episodeCategory);
        } else if (this.state.location.query.requestId) {
            if (!process) {
                process = await this.loadProcess(this.state.location.query.requestId);
            }
            if (process.episodeCategory) {
                await this.loadProcesses(patientId, process.episodeCategory);
            } else {
                await this.setState({ processes: [process] });
            }
        } else {
            throw Error('serviceId or requestId or episodeCategory required');
        }

        for (const processItem of this.state.processes) {
            await this.setProcessItemState(processItem.id);
        }

        // set episodeCategory in state
        if (this.state.processes[0] && this.state.processes[0].episodeCategory) {
            await this.setState({ episodeCategory: this.state.processes[0].episodeCategory });
        } else if (this.state.location.query.episodeCategory) {
            await this.setState({ episodeCategory: this.state.location.query.episodeCategory });
        }

        if (
            this.state.location.query.requestId &&
            !this.state.processes.find((item) => item.id === this.state.location.query.requestId)
        ) {
            await this.checkAbortedStatusForSelectedProcess();
        }

        await this.getFlexProcessesForCarePath(this.state.episodeCategory, this.state.patient?.id);

        await this.checkHasPatientMedicalHistory();

        // end setup
        await this.setState({ settingUpEpisode: false });
    }

    public render() {
        const isExternalHolter =
            this.state.isExternalHolter ?? this.props.router.searchParams.getOptional('isExternalHolter');
        const breadcrumbs: IBreadcrumbItem[] = [{ text: translateKey('patient.patients') }];
        if (this.state.patient) {
            breadcrumbs.push({ text: getDisplayName(this.state.patient), url: `/patients/${this.state.patient.id}` });
        }

        if (this.state.episodeCategory) {
            breadcrumbs.push({ text: translateKey(`episodeCategoryEnum.${this.state.episodeCategory}`) });
        }

        return (
            <Loading
                item={this.state.patient}
                loaded={(patient) => (
                    <PatientAccessReasons
                        onCancel={() => this.props.router.navigate(-1)}
                        onReasonGiven={() => this.load()}
                        patientId={patient.id}
                        requestId={this.state.location.query.requestId}
                    >
                        <EpisodeContextProvider
                            value={{
                                changeServiceIdForAddingProcess: (value) => this.changeServiceIdForAddingProcess(value),
                                changeShowRecommendations: (value) => this.setState({ showRecommendations: value }),
                                isExternalHolter,
                                onConvertToConsult: async (consultServiceId, procedureRequestId) =>
                                    await this.onServiceSelected({
                                        previousRequestId: procedureRequestId,
                                        serviceId: consultServiceId,
                                        withProcedure: false,
                                    }),
                                onConvertToReferral: async (referralServiceId, requestId) =>
                                    await this.onServiceSelected({
                                        previousRequestId: requestId,
                                        serviceId: referralServiceId,
                                        withProcedure: false,
                                    }),
                                patient,
                            }}
                        >
                            {({ autoSaveStatus }) => (
                                <FormGroupContext.Provider
                                    value={{ labelWidth: 'one-quarter', size: 'medium', variant: 'flushed' }}
                                >
                                    <PatientActiveInsuranceContextProvider patientId={patient.id}>
                                        <PageTemplate
                                            noGutterOnMobile={true}
                                            sideBarNavigation={
                                                <PatientSideBar
                                                    episodeCategory={this.state.episodeCategory}
                                                    onClickShowViewMedicalDossierPatientConsentModal={
                                                        this.handleViewMedicalDossierPatientConsentModalCancel
                                                    }
                                                    onReload={async () => await this.loadPatient(patient.id)}
                                                    patient={patient}
                                                    patientHasMedicalHistory={this.state.patientHasMedicalHistory}
                                                />
                                            }
                                            sideBarProfile={
                                                <PatientAvatarBlock
                                                    patient={patient}
                                                    size="large"
                                                />
                                            }
                                            topBarNavigation={
                                                <PatientSideBar
                                                    episodeCategory={this.state.episodeCategory}
                                                    onClickShowViewMedicalDossierPatientConsentModal={
                                                        this.handleViewMedicalDossierPatientConsentModalCancel
                                                    }
                                                    onReload={async () => await this.loadPatient(patient.id)}
                                                    patient={patient}
                                                />
                                            }
                                            topBarProfile={
                                                <PatientAvatarBlock
                                                    patient={patient}
                                                    size="small"
                                                />
                                            }
                                        >
                                            {/* START WRAPPER */}
                                            <UseUIStateWrapper>
                                                {(uiState) => {
                                                    return (
                                                        <>
                                                            {this.state.episodeCategory &&
                                                                this.props.authentication.organizationId && (
                                                                    <EpisodeHeader
                                                                        autoSaveStatus={this.getEpisodeHeaderStatus(
                                                                            autoSaveStatus
                                                                        )}
                                                                        bannerHeight={uiState?.bannerHeight}
                                                                        icon={episodeCategoryIconName(
                                                                            this.state.episodeCategory
                                                                        )}
                                                                        id="episode-header"
                                                                        title={`${translateKey(
                                                                            `episodeCategoryEnum.${this.state.episodeCategory}`
                                                                        )}`}
                                                                    >
                                                                        <EpisodeHeaderTabBar
                                                                            episodeCategory={this.state.episodeCategory}
                                                                            organizationId={
                                                                                this.props.authentication.organizationId
                                                                            }
                                                                            pathname={
                                                                                this.props.router.location.pathname
                                                                            }
                                                                            patientId={patient.id}
                                                                        />
                                                                    </EpisodeHeader>
                                                                )}

                                                            {this.state.showNoDefaultMessage &&
                                                                this.state.hasServices &&
                                                                this.state.processes.length === 0 &&
                                                                !this.state.settingUpEpisode && (
                                                                    <Alert appearance="info">
                                                                        {translateKey('service.noDefault')}
                                                                    </Alert>
                                                                )}

                                                            {this.state.patient &&
                                                                this.state.processes.length === 0 &&
                                                                !this.state.settingUpEpisode &&
                                                                this.state.episodeCategory && (
                                                                    <>
                                                                        {!this.state.selectedServiceType &&
                                                                            (!this.state.location.query.serviceId ||
                                                                                this.state.newProcessCancelled) && (
                                                                                <EmptyEpisode
                                                                                    episodeCategory={
                                                                                        this.state.episodeCategory
                                                                                    }
                                                                                    onClick={this.onServiceTypeSelected}
                                                                                    serviceTypes={this.adjustServiceTypesForInquiry()}
                                                                                />
                                                                            )}
                                                                        {this.state.selectedServiceType && (
                                                                            <ProcessStartWizard
                                                                                episodeCategory={
                                                                                    this.state.episodeCategory
                                                                                }
                                                                                patient={this.state.patient}
                                                                                serviceType={
                                                                                    this.state.selectedServiceType
                                                                                }
                                                                            >
                                                                                <NewProcessButtonStartBar
                                                                                    decisionAidPlacement="top"
                                                                                    defaultSelectedEpisodeCategory={
                                                                                        this.state.episodeCategory
                                                                                    }
                                                                                    defaultSelectedService={
                                                                                        this.state.location.query
                                                                                            .serviceId
                                                                                            ? {
                                                                                                  newProcessCustomIdentifier:
                                                                                                      undefined,
                                                                                                  serviceId:
                                                                                                      this.state
                                                                                                          .location
                                                                                                          .query
                                                                                                          .serviceId,
                                                                                                  withProcedure:
                                                                                                      this.state
                                                                                                          .location
                                                                                                          .query
                                                                                                          .withProcedure ===
                                                                                                      'true',
                                                                                              }
                                                                                            : undefined
                                                                                    }
                                                                                    defaultSelectedServiceType={
                                                                                        this.state.selectedServiceType
                                                                                    }
                                                                                    defaultTriggerOnClick={{
                                                                                        serviceType: true,
                                                                                    }}
                                                                                    hideEpisodeCategoryBar={true}
                                                                                    label={translateKey(
                                                                                        'home.startOne'
                                                                                    )}
                                                                                    onClear={this.clearNewProcessState}
                                                                                    onServiceIdSelected={
                                                                                        this.onServiceSelected
                                                                                    }
                                                                                    onServiceTypeSelected={
                                                                                        this.onServiceTypeSelected
                                                                                    }
                                                                                    onStartingPointSelected={
                                                                                        this.onStartingPointSelected
                                                                                    }
                                                                                    patient={{
                                                                                        id: this.state.patient.id,
                                                                                    }}
                                                                                />
                                                                            </ProcessStartWizard>
                                                                        )}
                                                                    </>
                                                                )}

                                                            {!this.state.settingUpEpisode &&
                                                                this.getProcesses().map((process) => (
                                                                    <Fragment key={process.id}>
                                                                        <Process
                                                                            disableFormAutosave={
                                                                                this.context?.frontendSettings
                                                                                    .disableFormAutosave ?? false
                                                                            }
                                                                            isPreviousRequest={this.isPreviousRequest(
                                                                                process
                                                                            )}
                                                                            onLoaded={async () =>
                                                                                this.onProcessRenderFinished()
                                                                            }
                                                                            onReloadPatient={async () =>
                                                                                await this.handleReloadPatient()
                                                                            }
                                                                            onServiceIdSelected={this.onServiceSelected}
                                                                            process={process}
                                                                            processItemState={this.getProcessItemState(
                                                                                process.id
                                                                            )}
                                                                            reloadSelfOrAllProcesses={async (
                                                                                reload: ReloadEpisode,
                                                                                requestId
                                                                            ) => {
                                                                                if (
                                                                                    reload === ReloadEpisode.reloadAll
                                                                                ) {
                                                                                    await this.loadProcesses(
                                                                                        patient.id,
                                                                                        this.state.episodeCategory ||
                                                                                            this.state.location.query
                                                                                                .episodeCategory,
                                                                                        true
                                                                                    );
                                                                                } else {
                                                                                    try {
                                                                                        await this.reloadProcess(
                                                                                            requestId
                                                                                        );
                                                                                    } catch (e) {
                                                                                        this.loadProcesses(
                                                                                            patient.id,
                                                                                            this.state
                                                                                                .episodeCategory ||
                                                                                                this.state.location
                                                                                                    .query
                                                                                                    .episodeCategory
                                                                                        );
                                                                                    }
                                                                                }
                                                                            }}
                                                                            removeProcess={async (requestId) =>
                                                                                await this.removeProcess(requestId)
                                                                            }
                                                                            scrollToProcessItem={async (
                                                                                requestId,
                                                                                scrollItem
                                                                            ) =>
                                                                                await this.scrollToProcessItem(
                                                                                    requestId,
                                                                                    scrollItem
                                                                                )
                                                                            }
                                                                            setHideCreateActions={async (value) =>
                                                                                this.onSetHideCreateActions(
                                                                                    value,
                                                                                    process.id
                                                                                )
                                                                            }
                                                                            updateProcessItemState={async (
                                                                                requestId,
                                                                                processState
                                                                            ) =>
                                                                                await this.setState(
                                                                                    ({ processItemStates }) => ({
                                                                                        processItemStates:
                                                                                            processItemStates.set(
                                                                                                requestId,
                                                                                                processState
                                                                                            ),
                                                                                    })
                                                                                )
                                                                            }
                                                                        />
                                                                        {this.state.add &&
                                                                            this.state.add.previousRequestId &&
                                                                            this.state.add.previousRequestId ===
                                                                                process.id && (
                                                                                <>
                                                                                    {this.renderRequestItemAddEdit(
                                                                                        patient
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                    </Fragment>
                                                                ))}

                                                            {this.state.add &&
                                                                !this.state.add.withProcedure &&
                                                                !hasOneAuthorization(
                                                                    ManagerAuthorization.patientProcesses
                                                                ) &&
                                                                !this.state.add.previousRequestId && (
                                                                    <>{this.renderRequestItemAddEdit(patient)}</>
                                                                )}

                                                            {this.state.add && this.state.add.withProcedure && (
                                                                <CollapseWrapper
                                                                    id="newProcess"
                                                                    processFormId={newProcessId}
                                                                >
                                                                    <ProcedureItemAddEdit
                                                                        disableFormAutosave={
                                                                            this.context?.frontendSettings
                                                                                .disableFormAutosave ?? false
                                                                        }
                                                                        formId={newProcessId}
                                                                        isFirstItem
                                                                        isLastItem
                                                                        isNewDirectProcedure
                                                                        onCancel={(requestId) =>
                                                                            requestId
                                                                                ? this.handleCancelOrClear(
                                                                                      requestId,
                                                                                      true
                                                                                  )
                                                                                : this.clearNewProcessState()
                                                                        }
                                                                        onReloadPatient={async () =>
                                                                            await this.handleReloadPatient()
                                                                        }
                                                                        onSave={async (requestId) => {
                                                                            await this.onRequestSaved(requestId);
                                                                        }}
                                                                        onServiceIdSelected={this.onServiceSelected}
                                                                        reloadProcess={async (
                                                                            reload?: ReloadEpisode
                                                                        ) => {
                                                                            if (reload === ReloadEpisode.reloadAll) {
                                                                                await this.loadProcesses(
                                                                                    patient.id,
                                                                                    this.state.episodeCategory ||
                                                                                        this.state.location.query
                                                                                            .episodeCategory,
                                                                                    true
                                                                                );
                                                                            }
                                                                        }}
                                                                        removeProcedure={this.clearNewProcessState}
                                                                        scrollToNewProcess={() => {
                                                                            if (this.state.processes.length > 0) {
                                                                                scrollToWithinContainer(
                                                                                    scrollContainerId,
                                                                                    'newProcess'
                                                                                );
                                                                            }
                                                                        }}
                                                                        serviceId={this.state.add.serviceId}
                                                                    />
                                                                </CollapseWrapper>
                                                            )}

                                                            {this.showProcessButtons() &&
                                                                this.state.allProcessesLoaded &&
                                                                !this.hideCreateActions() && (
                                                                    <ProcessStartWizard
                                                                        episodeCategory={this.state.episodeCategory}
                                                                        onAddProcessToEpisode={this.onServiceSelected}
                                                                        patient={this.state.patient}
                                                                    >
                                                                        <NewProcessButtonStartBar
                                                                            decisionAidPlacement="top"
                                                                            defaultSelectedEpisodeCategory={
                                                                                this.state.location.query
                                                                                    .episodeCategory
                                                                            }
                                                                            defaultSelectedService={
                                                                                this.state.location.query.serviceId
                                                                                    ? {
                                                                                          newProcessCustomIdentifier:
                                                                                              undefined,
                                                                                          serviceId:
                                                                                              this.state.location.query
                                                                                                  .serviceId,
                                                                                          withProcedure:
                                                                                              this.state.location.query
                                                                                                  .withProcedure ===
                                                                                              'true',
                                                                                      }
                                                                                    : undefined
                                                                            }
                                                                            hideEpisodeCategoryBar={true}
                                                                            label={translateKey('home.startOne')}
                                                                            onServiceIdSelected={this.onServiceSelected}
                                                                            onServiceTypeSelected={
                                                                                this.onServiceTypeSelected
                                                                            }
                                                                            onStartingPointSelected={
                                                                                this.onStartingPointSelected
                                                                            }
                                                                            patient={this.state.patient}
                                                                            scrollToBottom={() =>
                                                                                scrollToBottomWithinContainer(
                                                                                    scrollContainerId
                                                                                )
                                                                            }
                                                                        />
                                                                    </ProcessStartWizard>
                                                                )}
                                                        </>
                                                    );
                                                }}
                                            </UseUIStateWrapper>
                                            {/* END WRAPPERT */}
                                            <Modal
                                                actionButtons={[
                                                    <Button
                                                        color="secondary"
                                                        key="closeShort"
                                                        onClick={async () => {
                                                            this.updateBrowserHistoryUrl('requestId');
                                                            await this.setState({
                                                                showModalForCancelledProcess: false,
                                                            });
                                                        }}
                                                    >
                                                        {translateKey('common.closeShort')}
                                                    </Button>,
                                                ]}
                                                isOpen={this.state.showModalForCancelledProcess}
                                                onClose={async () =>
                                                    await this.setState({ showModalForCancelledProcess: false })
                                                }
                                                size="small"
                                                title={translateKey('request.cancelledRequest')}
                                            >
                                                <Paragraph>
                                                    {translateKey('request.cancelledRequestAfterSendingMail')}
                                                </Paragraph>
                                            </Modal>

                                            {this.renderFirstUseTour()}

                                            <ViewMedicalDossierPatientConsentModal
                                                isOpen={this.state.isViewMedicalDossierPatientConsentModalOpen}
                                                onCancel={this.handleViewMedicalDossierPatientConsentModalCancel}
                                                onSaved={this.handleViewMedicalDossierPatientConsentModalSaved}
                                                patientId={patient.id}
                                            />

                                            {this.state.showRecommendations && (
                                                <Box sx={{ mt: 10 }}>
                                                    <Recommendations />
                                                </Box>
                                            )}

                                            {this.state.hasFlexProcessesForCarePath && this.state.patient?.id && (
                                                <>
                                                    <Divider spacing="small" />
                                                    <Text>
                                                        <Link
                                                            to={`/patients/${this.state.patient.id}/${this.state.episodeCategory}`}
                                                            variant="link"
                                                        >
                                                            Ga naar recente zorgpaden
                                                        </Link>
                                                    </Text>
                                                </>
                                            )}
                                        </PageTemplate>
                                    </PatientActiveInsuranceContextProvider>
                                </FormGroupContext.Provider>
                            )}
                        </EpisodeContextProvider>
                    </PatientAccessReasons>
                )}
            />
        );
    }

    public async changeServiceIdForAddingProcess(newServiceId: string) {
        await this.setState(({ add }) => ({
            add: {
                ...add,
                serviceId: newServiceId,
            },
        }));
    }

    public renderFirstUseTour() {
        let showFirstUseTour = Boolean(this.state.allProcessesLoaded);
        if (showFirstUseTour) {
            if (this.state.add) {
                showFirstUseTour = Boolean(this.state.requestItemAddEditLoaded);
            }
        }

        if (showFirstUseTour) {
            return <FirstUseTour url="/episode" />;
        }

        return null;
    }

    @bind
    private async handleViewMedicalDossierPatientConsentModalSaved() {
        await this.checkHasPatientMedicalHistory();
        await this.toggleViewMedicalDossierPatientConsentModal();
    }

    @bind
    private async handleViewMedicalDossierPatientConsentModalCancel() {
        await this.toggleViewMedicalDossierPatientConsentModal();
    }

    @bind
    private async handleCancelOrClear(requestId: string, withProcedure: boolean = false) {
        await this.clearNewProcessState();

        if (!requestId) {
            return;
        }

        let entity = 'requests';
        let entityId = requestId;

        if (withProcedure) {
            const proceduresResult = await fetchJson(`/api/procedures?requestId=${requestId}`, 'GET');
            const procedures: IProcedure[] = proceduresResult.json.items;

            if (procedures.length > 0) {
                entity = 'procedures';
                entityId = procedures[0].id;
            }
        }

        const url = `/api/${entity}/${entityId}/cancel`;
        await fetchJson(url, 'POST', { reasonStopProccess: 'Cancelled by practitioner' });
        await this.removeProcess(requestId);
    }

    @bind
    private async clearNewProcessState() {
        await this.setState({
            add: undefined,
            location: { query: { ...this.state.location.query, serviceId: undefined } },
            previousRequest: undefined,
            selectedServiceType: undefined,
        });
    }

    private getProcesses() {
        const processes = this.getOrderedProcesses(this.state.processes);

        if (this.state.add && this.state.add.previousRequestId) {
            const previousRequest = this.state.processes.find(
                (process) => process.id === this.state.add!.previousRequestId
            );
            if (previousRequest && !processes.includes(previousRequest)) {
                processes.push(previousRequest);
            }
        }

        return processes;
    }

    private async getFlexProcessesForCarePath(episodeCategory: EpisodeCategory | null, patientId?: string) {
        if (patientId && episodeCategory && this.state.episodeCategory === EpisodeCategory.somnology) {
            const processes = await getProcesses(patientId, { episodeCategory }, this.abortController.signal);
            this.setState({ hasFlexProcessesForCarePath: !!processes?.length });
        }
    }

    @bind
    private async updateNewRequestPreviousRequest(requestId: string) {
        if (this.state.add) {
            await this.setState({ add: undefined });
        }

        await this.scrollToProcessItem(requestId, ProcessScrollItem.request);
        await this.openProcessItemState(requestId, true, { can_update: true });
    }

    private isPreviousRequest(process: IProcess) {
        const processes = this.getOrderedProcesses(this.state.processes);
        const index = processes.indexOf(process);
        const previousProcess = processes.find((item) => item.id === process.previousRequestId);
        const sequentialProcess = processes[index + 1];

        return (
            Boolean(
                sequentialProcess &&
                    (sequentialProcess.previousRequestId === process.id ||
                        (previousProcess && sequentialProcess.previousRequestId === process.previousRequestId))
            ) || Boolean(this.state.add && this.state.add.previousRequestId === process.id)
        );
    }

    private getChildNodes(list: { parent: IProcess | undefined; process: IProcess }[], parent: IProcess) {
        let nodes: { parent: IProcess | undefined; process: IProcess }[] = [];
        for (const item of list) {
            if (item.parent === parent) {
                nodes.push(item);
                nodes = nodes.concat(this.getChildNodes(list, item.process));
            }
        }

        return nodes;
    }

    private getOrderedProcesses(processes: IProcess[]) {
        const result = [];
        const nodeList: { parent: IProcess | undefined; process: IProcess }[] = [];

        for (const process of processes) {
            const previousRequest = processes.find(
                (previousProcess) => previousProcess.id === process.previousRequestId
            );
            nodeList.push({ parent: previousRequest, process });
        }

        const rootNodes = nodeList.filter((node) => !node.parent);

        for (const rootNode of rootNodes) {
            const rootProcessList = [rootNode.process];
            this.getChildNodes(nodeList, rootNode.process).forEach(
                (node) => node && rootProcessList.push(node.process)
            );

            rootProcessList.sort((a, b) => {
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            });
            result.push({
                mostRecentCreated_at: rootProcessList[rootProcessList.length - 1].created_at,
                processes: rootProcessList,
            });
        }

        result.sort((a, b) => {
            return new Date(a.mostRecentCreated_at).getTime() - new Date(b.mostRecentCreated_at).getTime();
        });

        const orderedProcesses = [];
        for (const cluster of result) {
            orderedProcesses.push(...cluster.processes);
        }

        return orderedProcesses;
    }

    private async loadTypeButtons() {
        const { serviceTypes, services } = await this.newProcessApi.loadServiceTypes();
        const episodeCategories = await this.newProcessApi.loadEpisodeCategories();

        const currentCategoryName =
            this.state.episodeCategory ||
            this.state.location.query.episodeCategory ||
            (this.props.router.searchParams.get('episodeCategory') ?? undefined);
        const currentCategory = episodeCategories.find((item) => item.name === currentCategoryName);

        if (!currentCategory) {
            return;
        }

        const filteredServiceTypes = uniq(
            services
                .filter((item) => {
                    return item.episodeCategoryId === currentCategory.id;
                })
                .filter((item) => {
                    return serviceTypes.includes(item.serviceType);
                })
                .map((item) => {
                    return item.serviceType;
                })
        ).sort(compareByServiceTypeButtonSortOrder);

        await this.setState({ serviceTypes: filteredServiceTypes });
    }

    @bind
    private async onStartingPointSelected(startingPoint: IServiceStartingPoint) {
        const patientId = this.props.router.searchParams.get('patientId');
        if (!patientId) {
            return;
        }
        const url = generateUrlForRoute(
            'patientCarePath',
            {
                activeTab: 'carePath',
                episodeCategory: startingPoint.service.episodeCategory.name,
                patientId,
            },
            {
                startingPointId: startingPoint.id,
            }
        );
        this.props.router.navigate(url);
    }

    @bind
    private async onServiceSelected(args: ServiceUrlObject) {
        const { isExternalHolter, newProcessCustomIdentifier, previousRequestId, serviceId, withProcedure } = args;
        if (serviceId) {
            if (previousRequestId) {
                await this.loadPreviousRequest(previousRequestId);
            }
            await this.setState({
                add: { newProcessCustomIdentifier, previousRequestId, serviceId, withProcedure },
                ...(isExternalHolter !== undefined && { isExternalHolter }),
            });
        } else {
            await this.setState({ add: undefined, isExternalHolter: undefined, previousRequest: undefined });
        }
    }

    @bind
    private async onServiceTypeSelected(serviceType: ServiceType) {
        await this.setState({ selectedServiceType: serviceType });
    }

    private async setProcessItemState(requestId: string, isOpen?: boolean, process?: IProcess) {
        if (!process) {
            process = this.state.processes.find((item) => item.id === requestId);
        }
        const episodeCategory = this.props.router.searchParams.get('episodeCategory') as EpisodeCategory | null;
        const isOpenActiveProcessesEnabled = episodeCategory
            ? isOpenActiveProcessesEnabledByEpisodeCategory(episodeCategory)
            : false;

        let isItemOpen = false;
        if (isOpenActiveProcessesEnabled) {
            isItemOpen = process ? isActiveProcess(process, this.state.processes) : false;
        } else {
            isItemOpen =
                this.state.location.query.requestId === requestId ||
                (!this.state.location.query.requestId &&
                    this.state.processes.length > 0 &&
                    this.state.processes[this.state.processes.length - 1].id === requestId) ||
                !!isOpen;
        }

        const editProcedure =
            this.state.location.query.editProcedure === 'true' &&
            this.state.location.query.requestId === requestId &&
            process?.procedure?.status === ProcedureStatus.inProgress &&
            this.props.authentication.id !== undefined &&
            process?.procedure?.performerPractitionerId === this.props.authentication.id;

        let diagnosticReportStates: Map<string, IReportItemState> = Map();
        if (process && process.diagnosticReports) {
            for (const report of process.diagnosticReports) {
                diagnosticReportStates = diagnosticReportStates.set(
                    report.id,
                    this.diagnosticReportItemState(isItemOpen, process)
                );
            }
        }

        const processItemState = {
            diagnosticReports: diagnosticReportStates,
            procedure: process?.procedure && this.procedureItemState(requestId, isItemOpen, editProcedure),
            request: this.requestItemState(requestId, isItemOpen),
            supervision: process && process.supervision ? this.supervisionItemState(isItemOpen) : undefined,
        };

        await this.setState(({ processItemStates }) => ({
            processItemStates: processItemStates.set(requestId, processItemState),
        }));
    }

    private getProcessItemState(requestId: string) {
        const processItem = this.state.processItemStates.get(requestId);
        if (!processItem) {
            throw new Error('Missing process item state for requestId: ' + requestId);
        }

        return processItem;
    }

    private async openProcessItemState(requestId: string): Promise<void>;
    private async openProcessItemState(
        requestId: string,
        isEditMode: true,
        authentication: { can_update: boolean }
    ): Promise<void>;
    private async openProcessItemState(
        requestId: string,
        isEditMode?: boolean,
        authentication?: { can_update: boolean }
    ) {
        const processItem = this.getProcessItemState(requestId);

        let diagnosticReports: Map<string, IReportItemState> = Map();
        if (processItem.diagnosticReports) {
            processItem.diagnosticReports.map((value, key) => {
                if (key && value) {
                    diagnosticReports = diagnosticReports.set(key, {
                        ...value,
                        initialIsOpen: true,
                        isClarificationOpen: true,
                        isOpen: true,
                    });
                }
            });
        }

        const request = this.state.processes.find((item) => item.id === requestId);
        const processItemRequest = { ...processItem.request };
        if (
            isEditMode &&
            request &&
            request.status === RequestStatus.draft &&
            authentication &&
            authentication.can_update
        ) {
            processItemRequest.mode = 'edit';
        }

        await this.setState(({ processItemStates }) => ({
            processItemStates: processItemStates.set(requestId, {
                diagnosticReports: diagnosticReports,
                procedure: processItem.procedure && { ...processItem.procedure, initialIsOpen: true, isOpen: true },
                request: { ...processItemRequest, initialIsOpen: true, isOpen: true },
                supervision: processItem.supervision && {
                    ...processItem.supervision,
                    initialIsOpen: true,
                    isGradingOpen: true,
                    isOpen: true,
                },
            }),
        }));
    }

    private renderRequestItemAddEdit(patient: IPatient) {
        if (!this.state.add) {
            return null;
        }

        return (
            <CollapseWrapper
                id="newProcess"
                processFormId={newProcessId}
            >
                <RequestItemAddEdit
                    disableFormAutosave={this.context?.frontendSettings.disableFormAutosave ?? false}
                    formId={newProcessId}
                    isFirstItem
                    isLastItem
                    newProcessCustomIdentifier={this.state.add.newProcessCustomIdentifier}
                    onCancel={(requestId) =>
                        requestId ? this.handleCancelOrClear(requestId) : this.clearNewProcessState()
                    }
                    onLoaded={() => this.requestItemAddEditLoaded()}
                    onReloadPatient={async () => await this.handleReloadPatient()}
                    onSave={async (requestId) => {
                        await this.onRequestSaved(requestId);
                    }}
                    previousRequest={this.state.previousRequest}
                    reloadProcess={async (reload?: ReloadEpisode) => {
                        if (reload === ReloadEpisode.reloadAll) {
                            await this.loadProcesses(
                                patient.id,
                                this.state.episodeCategory || this.state.location.query.episodeCategory,
                                true
                            );
                        }
                    }}
                    scrollToNewProcess={() => {
                        if (this.state.processes.length > 0) {
                            scrollToWithinContainer(scrollContainerId, 'newProcess');
                        }
                    }}
                    serviceId={this.state.add.serviceId}
                    updateNewRequestPreviousRequest={this.updateNewRequestPreviousRequest}
                />
            </CollapseWrapper>
        );
    }

    private async onRequestSaved(requestId: string) {
        await this.setState({ add: undefined, previousRequest: undefined, showRecommendations: true });
        await this.addRequestToRequestList(requestId);

        this.props.router.setSearchParams(
            (params: URLSearchParams) => {
                params.delete('serviceId');
                params.delete('withProcedure');
                params.set('requestId', requestId);

                return params;
            },
            { replace: true, state: {} }
        );
    }

    private requestItemAddEditLoaded() {
        this.setState({ requestItemAddEditLoaded: true });
    }

    private hideCreateActions() {
        let hideActions = false;
        this.state.processItemStates.valueSeq().forEach((processStateValue) => {
            if (processStateValue) {
                if (processStateValue.request.mode === 'edit') {
                    hideActions = true;

                    return;
                }

                if (
                    processStateValue.procedure &&
                    (processStateValue.procedure.mode === 'edit' || processStateValue.procedure.mode === 'add')
                ) {
                    hideActions = true;

                    return;
                }

                if (processStateValue.supervision && processStateValue.supervision.mode === 'edit') {
                    hideActions = true;

                    return;
                }

                if (processStateValue.newReportCategory) {
                    hideActions = true;

                    return;
                }

                processStateValue.diagnosticReports.valueSeq().forEach((diagnosticReportItemState) => {
                    if (
                        diagnosticReportItemState &&
                        (diagnosticReportItemState.mode === 'edit' || diagnosticReportItemState.mode === 'add')
                    ) {
                        hideActions = true;

                        return;
                    }
                });
            }
        });

        hideActions = hideActions || this.state.hideCreateActions;

        return hideActions;
    }

    private async loadPreviousRequest(requestId: string) {
        const url = `/api/requests/${requestId}`;
        const result = await fetchJson(url, 'GET');
        if (result.json.serviceType === ServiceType.examination) {
            const urlProcedure = `/api/requests/${requestId}/procedure?showAssets=true`;
            const resultProcedure = await fetchJson(urlProcedure, 'GET');

            result.json.procedure = resultProcedure.json;
        }

        await this.setState({ previousRequest: result.json });
    }

    private async addRequestToRequestList(requestId: string) {
        const process = await this.loadProcess(requestId);
        await this.setProcessItemState(requestId, true, process);
        await this.setState(({ processes }) => ({ processes: [...processes, process], showNoDefaultMessage: false }));
    }

    private async reloadProcess(requestId: string) {
        await this.setState({ hideCreateActions: false });
        const newProcess: IProcess = await this.loadProcess(requestId);

        const processes = this.state.processes.slice();
        const index = processes.findIndex((item) => item.id === requestId);
        if (index < 0) {
            throw new Error('Invalid index for reloading process requestId: ' + requestId);
        }
        processes[index] = newProcess;

        const oldProcess = this.state.processes[index];
        const oldProcessItemState = { ...this.getProcessItemState(requestId) };
        const newProcessItemState: IProcessItemState = { ...oldProcessItemState };

        if (oldProcess.procedure && !newProcess.procedure) {
            newProcessItemState.procedure = undefined;
        } else if (!oldProcess.procedure && newProcess.procedure) {
            newProcessItemState.procedure = this.procedureItemState(requestId, true);
        }

        for (const newReport of newProcess.diagnosticReports) {
            if (!oldProcess.diagnosticReports.find((item) => item.id === newReport.id)) {
                newProcessItemState.diagnosticReports = newProcessItemState.diagnosticReports.set(
                    newReport.id,
                    this.diagnosticReportItemState(true, newProcess)
                );
            }
        }

        for (const oldReport of oldProcess.diagnosticReports) {
            if (!newProcess.diagnosticReports.find((item) => item.id === oldReport.id)) {
                newProcessItemState.diagnosticReports = newProcessItemState.diagnosticReports.delete(oldReport.id);
            }
        }

        if (oldProcess.supervision && !newProcess.supervision) {
            newProcessItemState.supervision = undefined;
        } else if (
            !oldProcess.supervision &&
            newProcess.supervision &&
            newProcess.supervision.status === SupervisionStatus.completed
        ) {
            newProcessItemState.supervision = this.supervisionItemState(true);
        }

        await this.setState(({ processItemStates }) => ({
            processItemStates: processItemStates.set(requestId, newProcessItemState),
            processes,
        }));
    }

    private isFirstProcess(requestId: string) {
        return this.state.processes && this.state.processes.length > 0 && this.state.processes[0].id === requestId;
    }

    private requestItemState(requestId: string, isOpen: boolean): IItemState {
        return {
            initialIsOpen: isOpen,
            isFirstProcess: this.isFirstProcess(requestId),
            isOpen: isOpen,
            mode: 'read',
        };
    }

    private procedureItemState(requestId: string, isOpen: boolean, editProcedure?: boolean): IItemState {
        const mode = editProcedure ? 'edit' : 'read';

        return {
            initialIsOpen: isOpen,
            isFirstProcess: this.isFirstProcess(requestId),
            isOpen: isOpen,
            mode: mode,
        };
    }

    private diagnosticReportItemState(isProcessOpen: boolean, process: IProcess): IReportItemState {
        const isOpen =
            isProcessOpen || (process.task?.status === TaskStatus.open && process.task.type === TaskType.supervise);

        return {
            addClarification: false,
            initialIsOpen: isOpen,
            isClarificationOpen: isProcessOpen,
            isOpen: isOpen,
            mode: 'read',
        };
    }

    private supervisionItemState(isOpen: boolean): IItemState {
        return {
            initialIsOpen: isOpen,
            isGradingOpen: isOpen,
            isOpen: isOpen,
            mode: 'read',
        };
    }

    private scrollToProcessItem(requestId: string, scrollItem: ProcessScrollItem) {
        if (scrollItem === ProcessScrollItem.request) {
            scrollToWithinContainer(scrollContainerId, `${requestId}`);
        } else {
            scrollToWithinContainer(scrollContainerId, `${requestId}-${scrollItem}`);
        }
    }

    private async loadProcesses(patientId: string, episodeCategory?: string, updateProcessItemState?: boolean) {
        await this.setState({ hideCreateActions: false });

        const result = await getRequestProcesses({ episodeCategory, patientId, signal: this.abortController.signal });

        if (updateProcessItemState) {
            for (const newProcess of result) {
                if (!this.state.processes.find((item) => item.id === newProcess.id)) {
                    await this.setProcessItemState(newProcess.id, undefined, newProcess);
                }
            }

            for (const oldProcess of this.state.processes) {
                if (!result.find((item) => item.id === oldProcess.id)) {
                    await this.removeProcess(oldProcess.id);
                }
            }
        }

        this.numberOfProcessesStillLoading = result.length;
        await this.setState({
            allProcessesLoaded: isEmpty(result),
            processes: result,
        });
    }

    private async checkAbortedStatusForSelectedProcess() {
        const result = await this.abortableFetch.fetchJson(
            `/api/requests/${this.state.location.query.requestId}/status`,
            'GET'
        );
        if (result.json.status === RequestStatus.aborted || result.json.status === RequestStatus.cancelled) {
            await this.setState({ selectedProcessWasCancelled: true, showModalForCancelledProcess: true });
        }
    }

    private async loadPatient(patientId: string) {
        const url = `/api/patients/${patientId}?includeItemAuthorizations=canSeeProfessionalSummaryDataDetails,canSeePatientDetails,canUpdate,canSeePatientMedication,canSeePatientAllergies,canSeePatientLabResults,canSeePatientEpisodes,canUpdatePatientConsent`;

        const result = await this.abortableFetch.fetchJson(url, 'GET');

        await this.setState({ patient: result.json });
    }

    private async loadProcess(requestId: string) {
        const url = `/api/requestProcesses/${requestId}`;

        const result = await this.abortableFetch.fetchJson(url, 'GET');

        return result.json;
    }

    private async checkHasPatientMedicalHistory() {
        if (this.state?.patient?.id) {
            const result = await checkForProcessesClosedByOtherRequesterOrganizations(this.state.patient.id);
            await this.setState({ patientHasMedicalHistory: result });
        }
    }

    private async removeProcess(requestId: string) {
        let processes = this.state.processes.slice();
        processes = processes.filter((item) => item.id !== requestId);
        await this.setState(({ processItemStates }) => ({
            processItemStates: processItemStates.delete(requestId),
            processes: processes,
        }));

        if (processes.length === 0) {
            if (this.state.location.query.serviceId || !this.state.episodeCategory) {
                this.props.router.navigate(`/patients/${this.state.patient!.id}`);
            } else if (this.state.location.query.requestId === requestId) {
                this.updateBrowserHistoryUrl('requestId');
                await this.clearNewProcessState();
            }
        } else if (this.state.location.query.requestId === requestId) {
            this.updateBrowserHistoryUrl('requestId');
        }
    }

    private updateBrowserHistoryUrl(locationKeyToRemove: string) {
        this.props.router.setSearchParams((params) => {
            params.delete(locationKeyToRemove);

            return params;
        });
    }

    private async onProcessRenderFinished() {
        if (this.numberOfProcessesStillLoading > 1) {
            this.numberOfProcessesStillLoading -= 1;
        } else if (this.state.processes.length > 0) {
            if (this.state.scrolledToSelectedProcessOnLoaded) {
                // Care path has already mounted so this is only called when a new process is added, in that case scroll to it
                this.scrollToProcessItem(
                    this.state.processes[this.state.processes.length - 1].id,
                    ProcessScrollItem.request
                );
            } else {
                // If care path is mounted for the first time
                if (this.state.location.query.requestId && !this.state.selectedProcessWasCancelled) {
                    const requestId = await this.getCorrectRequestId();
                    const episodeCategory = this.props.router.searchParams.get(
                        'episodeCategory'
                    ) as EpisodeCategory | null;
                    const isOpenActiveProcessesEnabled = episodeCategory
                        ? isOpenActiveProcessesEnabledByEpisodeCategory(episodeCategory)
                        : false;
                    if (!isOpenActiveProcessesEnabled) {
                        await this.openProcessItemState(requestId);
                    }

                    const item = this.getProcessScrollItem(requestId);
                    this.scrollToProcessItem(requestId, item);
                } else {
                    const newProcess = document.getElementById(newProcessId);
                    if (newProcess) {
                        scrollToWithinContainer(scrollContainerId, newProcessId);
                    } else {
                        scrollToBottomWithinContainer(scrollContainerId);
                    }
                }
                await this.setState({ scrolledToSelectedProcessOnLoaded: true });
            }

            await this.setState({ allProcessesLoaded: true });
        }
    }

    private async getCorrectRequestId() {
        // when invitation, use previous counseling requestId
        if (
            this.state.location.query.requestId &&
            !this.state.processes.find((item) => item.id === this.state.location.query.requestId)
        ) {
            const process = await this.loadProcess(this.state.location.query.requestId);
            if (process && process.serviceType === ServiceType.invitation && process.previousRequestId) {
                return process.previousRequestId;
            }
        }

        return this.state.location.query.requestId;
    }

    private showProcessButtons() {
        return (
            this.state.patient &&
            this.state.episodeCategory &&
            !this.state.add &&
            this.state.processes.length > 0 &&
            this.state.processes[this.state.processes.length - 1].serviceId &&
            !hasOneAuthorization(ManagerAuthorization.patientProcesses)
        );
    }

    private adjustServiceTypesForInquiry() {
        const serviceTypes = [...this.state.serviceTypes];
        const serviceTypesIncludeInquiry = this.state.serviceTypes.includes(ServiceType.inquiry);
        const serviceTypesIncludeExamination = this.state.serviceTypes.includes(ServiceType.examination);

        if (serviceTypesIncludeInquiry) {
            if (!serviceTypesIncludeExamination) {
                serviceTypes.push(ServiceType.examination);
            }
            const inquiryFilteredServiceTypes = serviceTypes.filter(
                (serviceType) => serviceType !== ServiceType.inquiry
            );
            inquiryFilteredServiceTypes.sort(compareByServiceTypeButtonSortOrder);

            return inquiryFilteredServiceTypes;
        }

        return serviceTypes;
    }

    private async handleReloadPatient() {
        if (this.state.location.query.patientId) {
            await this.loadPatient(this.state.location.query.patientId);
        }
    }

    private async onSetHideCreateActions(value: boolean, requestId: string) {
        if (this.state.hideCreateActions !== value) {
            const isLastProcess =
                this.state.processes.map((process) => process.id).indexOf(requestId) ===
                this.state.processes.length - 1;

            if (isLastProcess) {
                await this.setState({ hideCreateActions: value });
            }
        }
    }

    private getProcessScrollItem(requestId: string): ProcessScrollItem {
        let item = ProcessScrollItem.request;
        const request = this.state.processes.find((process) => process.id === requestId);
        if (request) {
            if (request.supervision && request.supervision.status === SupervisionStatus.draft) {
                item = ProcessScrollItem.procedure;
            }
        }

        return item;
    }

    private async toggleViewMedicalDossierPatientConsentModal() {
        await this.setState({
            isViewMedicalDossierPatientConsentModalOpen: !this.state.isViewMedicalDossierPatientConsentModalOpen,
        });
    }

    private getEpisodeHeaderStatus(
        status: IEpisodeContext['autoSaveStatus']
    ): EpisodeHeaderProps['autoSaveStatus'] | undefined {
        switch (status) {
            case 'idle':
                return undefined;
            case 'saving':
                return {
                    state: 'saving',
                    text: translateKey('common.saving'),
                };
            case 'saved':
                return {
                    state: 'saved',
                    text: translateKey('common.saved'),
                };
            default:
                throw new Error(`Unknown auto save status: ${status}`);
        }
    }

    private hasAddOrEditModeInProcessItemState(processItemStates: Map<string, IProcessItemState>) {
        let hasAddOrEdit = false;
        processItemStates.forEach((newValue) => {
            if (newValue?.request?.mode === 'add' || newValue?.request?.mode === 'edit') {
                hasAddOrEdit = true;
            }
            if (newValue?.procedure?.mode === 'add' || newValue?.procedure?.mode === 'edit') {
                hasAddOrEdit = true;
            }
            if (newValue?.supervision?.mode === 'add' || newValue?.supervision?.mode === 'edit') {
                hasAddOrEdit = true;
            }
            if (newValue?.diagnosticReports?.size ?? 0 > 0) {
                newValue?.diagnosticReports.forEach((report) => {
                    if (report?.mode === 'add' || report?.mode === 'edit') {
                        hasAddOrEdit = true;
                    }
                });
            }
        });

        return hasAddOrEdit;
    }
}

export const Episode = connect(mapStateToProps)(withRouter(InnerEpisode));
